import { useState, useEffect, useCallback } from 'react';
import { useSnackbar } from 'notistack';
import useFetchWithMsal from '../../../hooks/useFetchWithMsal';
import { protectedResources } from '../../../authConfig';

const useInvoiceFiles = (invoice, fileAsBase64) => {
  const [allFiles, setAllFiles] = useState([]);
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);
  const [currentFileIndex, setCurrentFileIndex] = useState(0);
  const [error, setError] = useState(null);

  const { enqueueSnackbar } = useSnackbar();

  const { execute } = useFetchWithMsal({
    scopes: protectedResources.apiScopes.scopes.read,
  });

  useEffect(() => {
    if (invoice) {
      loadAllFiles();
    }
  }, [invoice, invoice?.status, invoice?.approvedBy, invoice?.rejectedBy]);

  const loadAllFiles = useCallback(async () => {
    if (!invoice) return;

    if (isLoadingFiles) {
      return;
    }

    setIsLoadingFiles(true);
    setError(null);

    try {
      const files = [];
      let originalFile = null;
      let signedFile = null;

      try {
        const isObjectId = /^[0-9a-fA-F]{24}$/.test(invoice._id || invoice.id);
        const identifier = isObjectId ? (invoice._id || invoice.id) : invoice.fileName;

        const endpoint = isObjectId
          ? `${process.env.REACT_APP_BACKEND_BASE_URL}/api/supplierInvoices/${identifier}`
          : `${process.env.REACT_APP_BACKEND_BASE_URL}/api/supplierInvoices/filename/${identifier}`

        const response = await execute('GET', endpoint);

        if (response && response.success && response.files) {
          if (response.files.original && response.invoice.hasOriginalVersion) {
            originalFile = {
              id: 'original_file',
              type: 'original',
              name: invoice.fileName ? invoice.fileName.replace('_signed', '') : 'original',
              displayName: 'חשבונית מקורית',
              fileContent: response.files.original,
              downloadUrl: null,
              isBase64: true,
              originalFileName: invoice.fileName ? invoice.fileName.replace('_signed', '') : 'original'
            };
          }

          if (response.files.signed) {
            signedFile = {
              id: 'signed_file',
              type: 'signed',
              name: invoice.fileName,
              displayName: 'חשבונית חתומה',
              fileContent: response.files.signed,
              downloadUrl: null,
              isBase64: true,
              originalFileName: invoice.fileName
            };
          }
        }
      } catch (apiError) {
        const signedFileName = invoice.fileName;
        const originalFileName = signedFileName?.replace('_signed', '');

        if (originalFileName && originalFileName !== signedFileName) {
          try {
            const originalFileResponse = await execute(
              'POST',
              `${process.env.REACT_APP_BACKEND_BASE_URL}/api/supplierInvoices/file/base64`,
              { fileName: originalFileName }
            );

            if (originalFileResponse && originalFileResponse.length > 1000) {
              originalFile = {
                id: 'original_file',
                type: 'original',
                name: originalFileName,
                displayName: 'חשבונית מקורית',
                fileContent: originalFileResponse,
                downloadUrl: null,
                isBase64: true,
                originalFileName: originalFileName
              };
            }
          } catch (originalError) {
            // Ignore error
          }
        }

        if (signedFileName) {
          try {
            const signedFileResponse = await execute(
              'POST',
              `${process.env.REACT_APP_BACKEND_BASE_URL}/api/supplierInvoices/file/base64`,
              { fileName: signedFileName }
            );

            if (signedFileResponse && signedFileResponse.length > 1000) {
              signedFile = {
                id: 'signed_file',
                type: 'signed',
                name: signedFileName,
                displayName: 'חשבונית חתומה',
                fileContent: signedFileResponse,
                downloadUrl: null,
                isBase64: true,
                originalFileName: signedFileName
              };
            }
          } catch (signedError) {
            // Ignore error
          }
        }
      }

      const isInvoiceApproved = invoice.approvedBy ||
        invoice.status === 'אושר' ||
        invoice.status === 'APPROVED' ||
        invoice.status?.toLowerCase() === 'approved';

      const isInvoiceRejected = invoice.rejectedBy ||
        invoice.status === 'נדחה' ||
        invoice.status === 'REJECTED' ||
        invoice.status?.toLowerCase() === 'rejected';

      const isInvoiceSigned = isInvoiceApproved || isInvoiceRejected;

      if (originalFile) {
        files.push(originalFile);
      }

      if (signedFile && isInvoiceSigned) {
        files.push(signedFile);
      }

      if (!originalFile && !signedFile && fileAsBase64) {
        const fallbackFile = {
          id: 'fallback_file',
          type: 'signed',
          name: invoice.fileName,
          displayName: 'חשבונית',
          fileContent: fileAsBase64,
          downloadUrl: null,
          isBase64: true,
          originalFileName: invoice.fileName
        };
        files.push(fallbackFile);
      }

      try {
        // Add cache-busting parameter to avoid browser/API caching of attachment list
        const cacheBuster = new Date().getTime();
        const attachmentsResponse = await execute(
          'GET',
          `${process.env.REACT_APP_BACKEND_BASE_URL}/api/attachments/attachments/${invoice._id || invoice.id}?_=${cacheBuster}`
        );

        if (attachmentsResponse && attachmentsResponse.attachments) {
          const filteredAttachments = attachmentsResponse.attachments.filter(attachment => {
            const attachmentName = (attachment.actualName || attachment.name || attachment.fileName || '').toLowerCase();
            const originalAttachmentName = (attachment.originalFileName || attachment.name || attachment.fileName || '').toLowerCase();
            const invoiceFileName = (invoice.fileName || '').toLowerCase();
            const originalFileNameWithoutExt = invoiceFileName.replace(/\.[^/.]+$/, '');

            const shouldExclude = attachmentName.includes(invoiceFileName) ||
              attachmentName.includes(originalFileNameWithoutExt) ||
              originalAttachmentName.includes(invoiceFileName) ||
              attachmentName.startsWith(invoice.booknum?.toString().toLowerCase());

            return !shouldExclude;
          });

          const attachmentFiles = filteredAttachments.map((attachment, index) => ({
            id: `attachment_${index}`,
            type: 'attachment',
            name: attachment.originalFileName || attachment.name || attachment.fileName,
            displayName: `צרופה: ${attachment.originalFileName || attachment.name || attachment.fileName}`,
            downloadUrl: attachment.downloadUrl,
            filePath: attachment.path,
            actualName: attachment.actualName || attachment.fileName,
            uploadedAt: attachment.uploadedAt,
            uploadedBy: attachment.uploadedBy,
            size: attachment.size,
            fileContent: null,
            isBase64: false,
            originalFileName: attachment.originalFileName || attachment.name || attachment.fileName
          }));

          files.push(...attachmentFiles);
        }
      } catch (attachmentError) {
        console.error('Error loading attachments:', attachmentError);
      }

      setAllFiles(files);
      setCurrentFileIndex(0);

    } catch (error) {
      console.error('Error loading invoice files:', error);
      setError(error.message);
      enqueueSnackbar(`שגיאה בטעינת קבצי החשבונית: ${error.message}`, { variant: 'error' });
    } finally {
      setIsLoadingFiles(false);
    }
  }, [invoice, fileAsBase64, execute, enqueueSnackbar, isLoadingFiles]);

  const loadFileContent = useCallback(async (fileId) => {
    const file = allFiles.find(f => f.id === fileId);
    if (!file || file.fileContent || file.isBase64) {
      return;
    }

    if (file.type !== 'attachment' || !file.name) {
      console.warn('Cannot load file content - file is not an attachment or missing name:', file);
      return;
    }

    try {
      const fileNameForDownload = file.originalFileName || file.name;

      const response = await execute(
        'GET',
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/attachments/download-attachment/${invoice._id || invoice.id}/${encodeURIComponent(fileNameForDownload)}`
      );

      if (!response || !response.success) {
        throw new Error(response?.message || 'No response from server');
      }

      let base64Content = response.fileContent;

      if (!base64Content.startsWith('data:')) {
        const contentType = response.contentType || 'application/octet-stream';
        base64Content = `data:${contentType};base64,${base64Content}`;
      }

      setAllFiles(prev => prev.map(f =>
        f.id === fileId
          ? { ...f, fileContent: base64Content, isBase64: true }
          : f
      ));

    } catch (error) {
      console.error('Error loading file content for file:', file.name, error);
      enqueueSnackbar(`שגיאה בטעינת הקובץ ${file.name}: ${error.message}`, { variant: 'error' });
    }
  }, [allFiles, enqueueSnackbar, execute, invoice]);

  const goToNextFile = useCallback(() => {
    if (currentFileIndex < allFiles.length - 1) {
      const nextIndex = currentFileIndex + 1;
      setCurrentFileIndex(nextIndex);
      const nextFile = allFiles[nextIndex];
      if (nextFile && !nextFile.fileContent && !nextFile.isBase64) {
        loadFileContent(nextFile.id);
      }
    }
  }, [currentFileIndex, allFiles, loadFileContent]);

  const goToPreviousFile = useCallback(() => {
    if (currentFileIndex > 0) {
      const prevIndex = currentFileIndex - 1;
      setCurrentFileIndex(prevIndex);
      const prevFile = allFiles[prevIndex];
      if (prevFile && !prevFile.fileContent && !prevFile.isBase64) {
        loadFileContent(prevFile.id);
      }
    }
  }, [currentFileIndex, allFiles, loadFileContent]);

  const goToFileByName = useCallback((fileName) => {
    if (!fileName || !allFiles.length) {
      return false;
    }

    const cleanFileName = fileName
      .replace(/^צרופה:\s*/, '')
      .trim();

    const fileIndex = allFiles.findIndex(file => {
      const fileName1 = file.name;
      const originalFileName1 = file.originalFileName;
      const displayName = file.displayName;

      const exactMatch =
        fileName1 === cleanFileName ||
        originalFileName1 === cleanFileName ||
        fileName1 === fileName ||
        originalFileName1 === fileName;

      if (exactMatch) {
        return true;
      }

      if (displayName &&
        (displayName.includes(cleanFileName) ||
          displayName.includes(fileName))) {
        return true;
      }

      if (fileName1.includes(cleanFileName) ||
        cleanFileName.includes(fileName1)) {
        return true;
      }

      if (file.type === 'attachment' &&
        (fileName1.toLowerCase() === cleanFileName.toLowerCase() ||
          fileName.toLowerCase().includes(fileName1.toLowerCase()) ||
          fileName1.toLowerCase().includes(fileName.toLowerCase()))) {
        return true;
      }

      return false;
    });

    if (fileIndex !== -1) {
      setCurrentFileIndex(fileIndex);

      const targetFile = allFiles[fileIndex];
      if (targetFile && !targetFile.fileContent && !targetFile.isBase64) {
        loadFileContent(targetFile.id);
      }

      return true;
    } else {
      return false;
    }
  }, [allFiles, loadFileContent]);

  const goToFile = useCallback((index) => {
    if (index >= 0 && index < allFiles.length) {
      setCurrentFileIndex(index);
      const targetFile = allFiles[index];
      if (targetFile && !targetFile.fileContent && !targetFile.isBase64) {
        loadFileContent(targetFile.id);
      }
    }
  }, [allFiles, loadFileContent]);

  const currentFile = allFiles[currentFileIndex] || null;

  useEffect(() => {
    if (currentFile && !currentFile.fileContent && !currentFile.isBase64) {
      loadFileContent(currentFile.id);
    }
  }, [currentFile, loadFileContent]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!allFiles.length > 1) return;

      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault();
          goToPreviousFile();
          break;
        case 'ArrowRight':
          event.preventDefault();
          goToNextFile();
          break;
        default:
          break;
      }
    };

    if (allFiles.length > 1) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [allFiles.length, goToNextFile, goToPreviousFile]);

  return {
    allFiles,
    currentFile,
    currentFileIndex,
    isLoadingFiles,
    error,
    totalFiles: allFiles.length,
    hasNextFile: currentFileIndex < allFiles.length - 1,
    hasPreviousFile: currentFileIndex > 0,
    goToNextFile,
    goToPreviousFile,
    goToFile,
    goToFileByName,
    refreshFiles: loadAllFiles
  };
};

export default useInvoiceFiles;